import {
  CheckboxField,
  ComputedFieldProps,
  DropdownOption,
  ErrorBanner,
  FormSection,
  LocalizedString,
  TextField,
  unsafeLocalizedString,
  useForm,
  Validator,
} from "@dbn/omnichannel-design-system-g4p";
import { array, boolean, option, record, taskEither } from "fp-ts";
import {
  constant,
  constFalse,
  constTrue,
  constUndefined,
  constVoid,
  pipe,
} from "fp-ts/function";
import { IO } from "fp-ts/IO";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { Option } from "fp-ts/Option";
import { ReaderTaskEither } from "fp-ts/ReaderTaskEither";
import { useEffect, useMemo, useState } from "react";
import { RuntimeLocaleKey, useFormatDate, useFormatMessage } from "../../intl";
import { CompanySuggestion } from "../../Common/CompanyField/domain";
import { useCompanyFields } from "../../Common/CompanyField/utils";
import { DropdownField } from "../../Common/DropdownField/DropdownField";
import {
  dropdownOptionToValue,
  selectedDropdownOption,
} from "../../Common/selectDropdownOption";
import { useValidators } from "../../Common/useValidators";
import { PromoForm } from "../../PhoneAndEmailVerification/PromoForm";
import {
  foldPersonalDataField,
  GenericPersonalDataField,
  PersonalData,
  PersonalDataBooleanField,
  PersonalDataField,
  PersonalDataFromOutput,
  PersonalDataNumberField,
  PersonalDataOption,
} from "../Confirmation/domain";
import { useReworkPersonalDataComparator } from "../Rework/useReworkComparator";
import { useQuery } from "../../useAPI";
import { promoCodeRead } from "../../PhoneAndEmailVerification/api";
import * as remoteData from "../../RemoteData";
import { useAppContext } from "../../useAppContext";
import { foldTenant, MaritalStatus } from "../../globalDomain";
import { DateFieldComp } from "../../Common/Date/DateFieldComp";
import { NumberField } from "../../Common/NumberField/NumberField";
import { YesNoRadioGroupField } from "../../Common/YesNoRadioGroup/YesNoRadioGroupField";

type Props = {
  personalData: PersonalData;
  canSubmit: boolean;
  onDataReady: ReaderTaskEither<PersonalData, unknown, unknown>;
  onFailure: IO<unknown>;
  reworkData: Option<PersonalDataFromOutput>;
  prefill: boolean;
};

export function PersonalDataForm(props: Props) {
  const {
    config: { socialInsuranceEnabled },
    apiParameters: { tenant },
  } = useAppContext();
  const formatMessage = useFormatMessage();
  const formatDate = useFormatDate("dd-mm-yyyy");
  const [promoCodeQueryResult] = useQuery(promoCodeRead);

  const { formatIssues, isDisabled } = useReworkPersonalDataComparator(
    props.reworkData
  );

  const {
    nonBlankString,
    validDate,
    defined,
    definedNoExtract,
    maxLength,
    validCompanyIcoRequired,
    nonNegativeNumber,
    minAmountValidator,
  } = useValidators();

  const CompanyFields = useCompanyFields();
  const [isOkPromoCodeForm, setIsOkPromoCodeForm] = useState<boolean>(true);
  const [showCodeInputError, setShowCodeInputError] = useState<boolean>(false);
  const [companyIcoIsValid, setCompanyIcoIsValid] = useState(true);
  const [isMarried, setMarried] = useState(true);

  const isSkTenant = foldTenant(tenant, constTrue, constFalse);
  const displaySocialInsurance = isSkTenant && socialInsuranceEnabled;

  const { fieldProps, handleSubmit, setValues, values } = useForm(
    {
      initialValues: pipe(
        props.personalData,
        array.reduce<
          PersonalDataField,
          Record<RuntimeLocaleKey, PersonalDataField>
        >({}, (res, field) => ({
          ...res,
          [field.key]: {
            ...field,
            value: props.prefill ? field.value : option.none,
          },
        }))
      ),
      fieldValidators: values =>
        record.map(
          foldPersonalDataField<
            Validator<PersonalDataField, PersonalDataField> | undefined
          >(
            constUndefined,
            field => () =>
              pipe(
                validDate(field.value),
                taskEither.chain(() => taskEither.fromIO(() => field))
              ),
            field => () =>
              pipe(
                defined<number>()(field.value),
                taskEither.chain(nonNegativeNumber),
                taskEither.chain(() => taskEither.fromIO(() => field))
              ),
            field => () =>
              pipe(
                defined<string>()(field.value),
                taskEither.chain(nonBlankString),
                taskEither.chain(() => taskEither.fromIO(() => field))
              ),
            field => () =>
              isSkTenant
                ? pipe(
                    defined<number>()(field.value),
                    taskEither.chain(nonNegativeNumber),
                    taskEither.chain(
                      minAmountValidator(
                        isMarried ? 2 : 1,
                        isMarried
                          ? formatMessage(
                              "FormFieldError.adultsMinAmountWhenMarried"
                            )
                          : formatMessage(
                              "FormFieldError.adultsMinAmountWhenNotMarried",
                              { minAdults: 1 }
                            )
                      )
                    ),
                    taskEither.chain(() => taskEither.fromIO(() => field))
                  )
                : pipe(
                    defined<number>()(field.value),
                    taskEither.chain(nonNegativeNumber),
                    taskEither.chain(() => taskEither.fromIO(() => field))
                  ),
            field => () =>
              pipe(
                defined<string>()(field.value),
                taskEither.chain(
                  validCompanyIcoRequired(false, companyIcoIsValid)
                ),
                taskEither.chain(() => taskEither.fromIO(() => field))
              ),
            field => () =>
              pipe(
                defined<string>()(field.value),
                taskEither.chain(nonBlankString),
                taskEither.chain(maxLength(200)),
                taskEither.chain(() => taskEither.fromIO(() => field))
              ),
            field => () =>
              pipe(
                pipe(
                  ((values as Record<string, PersonalDataField>)[
                    "NumberOfChildrenInHousehold"
                  ] as PersonalDataNumberField)?.value as Option<number>,
                  option.exists(val => val > 0)
                ) && displaySocialInsurance,
                boolean.fold(
                  () => taskEither.fromIO(() => field as PersonalDataField),
                  () =>
                    pipe(
                      definedNoExtract<boolean>()(field.value),
                      taskEither.chain(() =>
                        taskEither.fromIO(() => field as PersonalDataField)
                      )
                    )
                )
              ),
            field => () =>
              pipe(
                pipe(
                  ((values as Record<string, PersonalDataField>)[
                    "ShowAmountOfTaxBonus"
                  ] as PersonalDataBooleanField)?.value as Option<boolean>,
                  option.getOrElse(constFalse)
                ) && displaySocialInsurance,
                boolean.fold(
                  () =>
                    taskEither.fromIO(
                      () =>
                        ({ ...field, value: option.none } as PersonalDataField)
                    ),
                  () =>
                    pipe(
                      defined<number>()(field.value),
                      taskEither.chain(nonNegativeNumber),
                      taskEither.chain(() =>
                        taskEither.fromIO(() => field as PersonalDataField)
                      )
                    )
                )
              )
          )
        )(values),
    },
    {
      onSubmit: data => () =>
        pipe(
          isOkPromoCodeForm,
          boolean.fold(
            taskEither.fromIO(() => {
              setShowCodeInputError(true);
              props.onFailure();
            }),
            props.onDataReady(Object.values(data))
          )
        ),
    }
  );

  useEffect(() => {
    setMarried(
      fieldProps("MaritalStatus" as RuntimeLocaleKey) &&
        pipe(
          (fieldProps("MaritalStatus" as RuntimeLocaleKey)
            .value as DropdownOption<Option<MaritalStatus>>).value,
          option.exists(status => status === "Married")
        )
    );
  }, [fieldProps("MaritalStatus" as RuntimeLocaleKey).value]);

  useEffect(() => {
    // @ts-ignore
    if (values["NumberOfPersonsInHousehold"]) {
      fieldProps("NumberOfPersonsInHousehold" as RuntimeLocaleKey)?.onChange(
        fieldProps("NumberOfPersonsInHousehold" as RuntimeLocaleKey)?.value
      );
    }
  }, [isMarried]);

  useEffect(() => {
    const timeout = setTimeout(
      () =>
        pipe(
          props.canSubmit,
          boolean.fold(
            constVoid,
            pipe(
              handleSubmit,
              taskEither.orElse(() => taskEither.fromIO(props.onFailure))
            )
          )
        ),
      0
    );
    return () => {
      clearTimeout(timeout);
    };
  }, [props.canSubmit]);

  const [isFormInErrorState, companyICO, companyName] = useMemo(() => {
    let companyICO: Option<PersonalDataField> = option.none;
    let companyName: Option<PersonalDataField> = option.none;
    let errorState = false;

    props.personalData.forEach(field => {
      if (option.isSome(field.autocomplete)) {
        switch (field.autocomplete.value) {
          case "CompanyICO":
            if (option.isNone(companyICO)) {
              companyICO = option.some(field);
            } else {
              console.error(
                `Found multiple autocomplete company ICO field ${field.key}`
              );
              errorState = true;
            }
            break;

          case "CompanyName":
            if (option.isNone(companyName)) {
              companyName = option.some(field);
            } else {
              console.error(
                `Found multiple autocomplete company name field ${field.key}`
              );
              errorState = true;
            }
            break;
        }
      }
    });

    return [errorState, companyICO, companyName];
  }, [props.personalData]);

  const syncCompanyFields = (suggestion: CompanySuggestion) => {
    if (option.isSome(companyICO)) {
      // companyICO.value.value = suggestion.id;

      setValues({
        [companyICO.value.key]: { ...companyICO.value, value: suggestion.id },
      });
    }

    if (option.isSome(companyName)) {
      // companyName.value.value = suggestion.name;

      setValues({
        [companyName.value.key]: {
          ...companyName.value,
          value: suggestion.name,
        },
      });
    }
  };

  function toFieldPropsWithIssues<T, O = T>(
    props: ComputedFieldProps<GenericPersonalDataField<T, O>>
  ): ComputedFieldProps<T> & { key: string } {
    return {
      ...props,
      key: props.name,
      value: props.value.value,
      onChange: value =>
        props.onChange({
          ...props.value,
          value,
        }),
      issues: formatIssues(props.name, props.issues),
      disabled: props.disabled || isDisabled(props.name),
    };
  }

  const onCompanyIcoValidationChange = (isValid: boolean) => {
    setCompanyIcoIsValid(isValid);
  };

  return pipe(
    isFormInErrorState,
    boolean.fold(
      () => (
        <FormSection>
          {pipe(
            props.personalData,
            array.map(
              foldPersonalDataField(
                field => {
                  const computedFieldProps = toFieldPropsWithIssues(
                    (fieldProps(field.key) as unknown) as ComputedFieldProps<
                      GenericPersonalDataField<Option<boolean>>
                    >
                  );

                  return (
                    <CheckboxField
                      label={formatMessage(field.key)}
                      {...computedFieldProps}
                      value={pipe(computedFieldProps.value, option.toUndefined)}
                      onChange={val =>
                        computedFieldProps.onChange(option.some(val))
                      }
                    />
                  );
                },
                field => {
                  const {
                    value,
                    onChange,
                    ...computedFieldProps
                  } = toFieldPropsWithIssues(
                    (fieldProps(field.key) as unknown) as ComputedFieldProps<
                      GenericPersonalDataField<Option<Date>>
                    >
                  );

                  return pipe(
                    field.options,
                    option.fold(
                      () => (
                        <DateFieldComp
                          label={formatMessage(field.key)}
                          placeholder={formatMessage(field.key)}
                          {...computedFieldProps}
                          value={option.some(value)}
                          onChange={option.fold(constVoid, onChange)}
                        />
                      ),
                      options => {
                        const dropdownOptions = options.map(opt => ({
                          label: pipe(
                            opt.name,
                            option.map(formatMessage),
                            option.getOrElse(() => formatDate(opt.value))
                          ),
                          value: opt.value,
                        }));

                        const dropdownValue = pipe(
                          dropdownOptions,
                          array.findFirst(
                            option_ =>
                              option.isSome(value) &&
                              option_.value.toISOString() ===
                                value.value.toISOString()
                          )
                        );

                        return (
                          <DropdownField
                            label={formatMessage(field.key)}
                            placeholder={formatMessage(field.key)}
                            {...computedFieldProps}
                            value={dropdownValue}
                            onChange={val => {
                              pipe(val, dropdownOptionToValue, onChange);
                            }}
                            options={dropdownOptions}
                            clearable={false}
                            searchable={false}
                          />
                        );
                      }
                    )
                  );
                },
                field => {
                  const computedFieldProps = toFieldPropsWithIssues(
                    (fieldProps(field.key) as unknown) as ComputedFieldProps<
                      GenericPersonalDataField<Option<number>, number>
                    >
                  );

                  return pipe(
                    field.options as Option<
                      NonEmptyArray<PersonalDataOption<number>>
                    >,
                    option.fold(
                      () => (
                        <NumberField
                          label={formatMessage(field.key)}
                          placeholder={formatMessage(field.key)}
                          {...computedFieldProps}
                          rightContent={option.none}
                        />
                      ),
                      options => {
                        const dropdownOptions: DropdownOption<number>[] = options.map(
                          opt => ({
                            label: pipe(
                              opt.name,
                              option.map(formatMessage),
                              option.getOrElse(() =>
                                unsafeLocalizedString(opt.value.toString(10))
                              )
                            ),
                            value: opt.value,
                          })
                        );

                        return (
                          <DropdownField
                            label={formatMessage(field.key)}
                            placeholder={formatMessage(field.key)}
                            {...computedFieldProps}
                            value={selectedDropdownOption(
                              computedFieldProps.value,
                              dropdownOptions
                            )}
                            onChange={val => {
                              computedFieldProps.onChange(
                                dropdownOptionToValue(val)
                              );
                            }}
                            options={dropdownOptions}
                            clearable={false}
                            searchable={false}
                          />
                        );
                      }
                    )
                  );
                },
                field => {
                  const {
                    value,
                    onChange,
                    ...computedFieldProps
                  } = toFieldPropsWithIssues(
                    (fieldProps(field.key) as unknown) as ComputedFieldProps<
                      GenericPersonalDataField<Option<LocalizedString>>
                    >
                  );

                  return pipe(
                    field.options,
                    option.fold(
                      () => (
                        <TextField
                          label={formatMessage(field.key)}
                          placeholder={formatMessage(field.key)}
                          {...computedFieldProps}
                          value={pipe(value, option.getOrElseW(constant("")))}
                          onChange={value =>
                            onChange(option.some(value as LocalizedString))
                          }
                        />
                      ),
                      options => {
                        const dropdownOptions = options.map(opt => ({
                          label: pipe(
                            opt.name,
                            option.map(formatMessage),
                            option.getOrElse(() => opt.value)
                          ),
                          value: opt.value,
                        }));
                        return (
                          <DropdownField
                            label={formatMessage(field.key)}
                            placeholder={formatMessage(field.key)}
                            {...computedFieldProps}
                            value={selectedDropdownOption(
                              value,
                              dropdownOptions
                            )}
                            onChange={val => {
                              pipe(val, dropdownOptionToValue, onChange);
                            }}
                            options={dropdownOptions}
                            clearable={false}
                            searchable={false}
                          />
                        );
                      }
                    )
                  );
                },
                field => {
                  const computedFieldProps = toFieldPropsWithIssues(
                    (fieldProps(field.key) as unknown) as ComputedFieldProps<
                      GenericPersonalDataField<Option<number>, number>
                    >
                  );
                  return (
                    <NumberField
                      label={formatMessage(field.key)}
                      placeholder={formatMessage(field.key)}
                      {...computedFieldProps}
                      rightContent={option.none}
                    />
                  );
                },
                field => {
                  const computedFieldProps = toFieldPropsWithIssues(
                    (fieldProps(field.key) as unknown) as ComputedFieldProps<
                      GenericPersonalDataField<Option<string>>
                    >
                  );

                  return (
                    <CompanyFields.Name
                      showNativeTooltip
                      {...computedFieldProps}
                      label={formatMessage(field.key)}
                      placeholder={formatMessage(field.key)}
                      onSelectSuggestion={syncCompanyFields}
                      value={pipe(
                        computedFieldProps.value,
                        option.getOrElse(constant(""))
                      )}
                      onChange={val =>
                        computedFieldProps.onChange(option.some(val))
                      }
                      // onValidationChange={onValidationChange}
                    />
                  );
                },
                field => {
                  const computedFieldProps = toFieldPropsWithIssues(
                    (fieldProps(field.key) as unknown) as ComputedFieldProps<
                      GenericPersonalDataField<Option<string>>
                    >
                  );

                  return (
                    <CompanyFields.ICO
                      showNativeTooltip
                      {...computedFieldProps}
                      value={pipe(
                        computedFieldProps.value,
                        option.getOrElse(constant(""))
                      )}
                      onChange={val =>
                        computedFieldProps.onChange(option.some(val))
                      }
                      label={formatMessage(field.key)}
                      placeholder={formatMessage(field.key)}
                      onSelectSuggestion={syncCompanyFields}
                      onCompanyIcoValidationChange={
                        onCompanyIcoValidationChange
                      }
                    />
                  );
                },
                field => {
                  const computedFieldProps = toFieldPropsWithIssues(
                    (fieldProps(field.key) as unknown) as ComputedFieldProps<
                      GenericPersonalDataField<Option<boolean>>
                    >
                  );

                  return (
                    <>
                      {pipe(
                        (fieldProps(
                          "NumberOfChildrenInHousehold" as RuntimeLocaleKey
                        )?.value as PersonalDataNumberField)
                          ?.value as Option<number>,
                        option.exists(val => val > 0)
                      ) &&
                        displaySocialInsurance && (
                          <YesNoRadioGroupField
                            {...computedFieldProps}
                            description={formatMessage(
                              "StandardLoan.EmployedCard.NumberOfChildrenInHouseholdTooltip"
                            )}
                            value={computedFieldProps.value}
                            onChange={computedFieldProps.onChange}
                            label={formatMessage(field.key)}
                          />
                        )}
                    </>
                  );
                },
                field => {
                  const computedFieldProps = toFieldPropsWithIssues(
                    (fieldProps(field.key) as unknown) as ComputedFieldProps<
                      GenericPersonalDataField<Option<number>, number>
                    >
                  );
                  return (
                    <>
                      {pipe(
                        (fieldProps("ShowAmountOfTaxBonus" as RuntimeLocaleKey)
                          ?.value as PersonalDataBooleanField)
                          ?.value as Option<boolean>,
                        option.fromNullable,
                        option.flatten,
                        option.getOrElseW(constFalse)
                      ) &&
                        displaySocialInsurance && (
                          <NumberField
                            description={formatMessage(
                              "StandardLoan.EmployedCard.taxBonusAmountTooltip"
                            )}
                            label={formatMessage(field.key)}
                            placeholder={formatMessage(field.key)}
                            {...computedFieldProps}
                            rightContent={option.none}
                            max={999999999}
                            min={0}
                            formatOptions={{ maximumFractionDigits: 3 }}
                            locale="sk"
                          />
                        )}
                    </>
                  );
                }
              )
            )
          )}
          {option.isNone(props.reworkData) &&
            pipe(
              promoCodeQueryResult,
              remoteData.toOption,
              option.map(({ ValidPromotionCode }) =>
                pipe(
                  ValidPromotionCode,
                  option.fold(
                    () => (
                      <PromoForm
                        initialPromoCode={option.none}
                        onApply={constVoid}
                        onRemove={constVoid}
                        onValidFormChange={isValid => {
                          setIsOkPromoCodeForm(isValid);
                          pipe(
                            isValid,
                            boolean.fold(constVoid, () =>
                              setShowCodeInputError(false)
                            )
                          );
                        }}
                        showError={showCodeInputError}
                      />
                    ),
                    ({ promoCode, isCodeConsumed, landingPageText }) => (
                      <PromoForm
                        initialPromoCode={option.some(promoCode)}
                        onApply={constVoid}
                        onRemove={constVoid}
                        readonly={isCodeConsumed}
                        message={landingPageText}
                        onValidFormChange={isValid => {
                          setIsOkPromoCodeForm(isValid);
                          pipe(
                            isValid,
                            boolean.fold(constVoid, () =>
                              setShowCodeInputError(false)
                            )
                          );
                        }}
                        showError={showCodeInputError}
                      />
                    )
                  )
                )
              ),
              option.toNullable
            )}
        </FormSection>
      ),
      () => <ErrorBanner children={formatMessage("GenericError")} />
    )
  );
}
